import {
  Box,
  CrudMode,
  formatCurrency,
  formatPercent,
  Grid,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { NonPersonnel, NonPersonnelEditResource, NonPersonnelViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const AmountsInfo = (props: {
  crudMode: CrudMode;
  nonPersonnel: NonPersonnel;
  setNonPersonnel: (value: NonPersonnel) => void;
  viewResource: NonPersonnelViewResource;
  editResource: NonPersonnelEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const rowBreakpoints = setBreakpoints({ lg: 4, md: 6, xs: 12 });
  const labelXs: number = 5;
  const valueXs: number = 7;

  const salaryField = () => {
    const propertyName = pn<NonPersonnel>("salary");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.salary)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.salary}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.salary = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const benefitField = () => {
    const propertyName = pn<NonPersonnel>("benefit");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.benefit)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.benefit}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.benefit = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const travelField = () => {
    const propertyName = pn<NonPersonnel>("travel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.travel)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.travel}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.travel = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const suppliesField = () => {
    const propertyName = pn<NonPersonnel>("supplies");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.supplies)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.supplies}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.supplies = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const suppliesDetailField = () => {
    const propertyName = pn<NonPersonnel>("suppliesDetailId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && props.nonPersonnel.suppliesDetail?.name}
          {props.crudMode === "edit" && (
            <Select
              modelProperty={property}
              errors={props.validationErrors[propertyName]}
              value={props.editResource.suppliesDetails?.find((x) => x.id === props.nonPersonnel.suppliesDetailId)}
              options={props.editResource.suppliesDetails}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.suppliesDetailId = value?.id;
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const consultantField = () => {
    const propertyName = pn<NonPersonnel>("consultant");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.consultant)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.consultant}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.consultant = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const otherField = () => {
    const propertyName = pn<NonPersonnel>("other");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.other)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.other}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.other = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const otherDescriptionField = () => {
    const propertyName = pn<NonPersonnel>("otherDescription");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && props.nonPersonnel.otherDescription}
          {props.crudMode === "edit" && (
            <Input
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.otherDescription}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.otherDescription = e.target.value;
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const equipmentField = () => {
    const propertyName = pn<NonPersonnel>("equipment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.equipment)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.equipment}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.equipment = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const indirectCostAmountField = () => {
    const propertyName = pn<NonPersonnel>("indirectCostAmount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs}>
          <Typography sx={{ color: "#08519c" }} variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          <Typography sx={{ color: "#08519c" }}>
            {formatCurrency(props.nonPersonnel.indirectCostAmount)}</Typography>
        </Grid>
      </Grid>
    );
  };

  const indirectPercentField = () => {
    const propertyName = pn<NonPersonnel>("indirectPercent");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs}>
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {formatPercent(props.nonPersonnel.indirectPercent)}
        </Grid>
      </Grid>
    );
  };

  const scholarshipField = () => {
    const propertyName = pn<NonPersonnel>("scholarship");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          {props.crudMode === "view" && formatCurrency(props.nonPersonnel.scholarship)}
          {props.crudMode === "edit" && (
            <Input
              type="number"
              required={property?.required}
              maxLength={property?.maxLength}
              errors={props.validationErrors[propertyName]}
              value={props.nonPersonnel.scholarship}
              onChange={(e) =>
                props.setNonPersonnel(
                  produce(props.nonPersonnel, (draft) => {
                    draft.scholarship = parseFloat(e.target.value);
                  })
                )
              }
              size="small"
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const totalAmountField = () => {
    const propertyName = pn<NonPersonnel>("totalAmount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid container spacing="sm">
        <Grid item xs={labelXs} display="flex" alignItems="center">
          <Typography sx={{ color: "#08519c" }} variant="body2">{property?.displayName}</Typography>
        </Grid>
        <Grid item xs={valueXs}>
          <Typography sx={{ color: "#08519c" }}>
            {props.crudMode === "view" && formatCurrency(props.nonPersonnel.totalAmount)}</Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <Stack ref={breakpointsRef} direction={"column"} spacing="sm">
      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {salaryField()}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {benefitField()}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {travelField()}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {suppliesField()}
          </Grid>

          {props.nonPersonnel.supplies !== undefined && props.nonPersonnel.supplies > 0 && (
            <Grid item xs={rowBreakpoints}>
              {suppliesDetailField()}
            </Grid>
          )}
        </Grid>
      </Box>

      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {consultantField()}
          </Grid>
        </Grid>
      </Box>

      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {otherField()}
          </Grid>

          {props.nonPersonnel.other !== undefined && props.nonPersonnel.other > 0 && (
            <Grid item xs={rowBreakpoints}>
              {otherDescriptionField()}
            </Grid>
          )}
        </Grid>
      </Box>

      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {equipmentField()}
          </Grid>
        </Grid>
      </Box>

      {props.crudMode === "view" && (
        <Box>
          <Grid container spacing="md">
            <Grid item xs={rowBreakpoints}>
              {indirectCostAmountField()}
            </Grid>

            {props.nonPersonnel.indirectPercent !== undefined && (
              <Grid item xs={rowBreakpoints}>
                {indirectPercentField()}
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Box>
        <Grid container spacing="md">
          <Grid item xs={rowBreakpoints}>
            {scholarshipField()}
          </Grid>
        </Grid>
      </Box>

      {props.crudMode === "view" && (
        <Box>
          <Grid container spacing="md">
            <Grid item xs={rowBreakpoints}>
              {totalAmountField()}
            </Grid>
          </Grid>
        </Box>
      )}
    </Stack>
  );
};
